// src/modules/app-views/_apps/appmulti/plans/pages/Document/decorators/request.js
// *******************************************************************************
// REQUEST DECORATOR
// Transforms data before sending to the server
// *******************************************************************************

import plansService from 'modules/app-views/_apps/appmulti/plans/service';

const decorateRequest = ctx => {
  const { data, allValues } = ctx;

  if (!data) return data;

  const transformed = { ...data };

  // Ensure priceType is set
  if (!transformed.priceType && allValues?.priceType) {
    transformed.priceType = allValues.priceType;
  }

  // Ensure currency is set
  if (!transformed.currency && allValues?.currency) {
    transformed.currency = allValues.currency;
  }

  // Handle operatorId from select
  // data.operatorId holds the user's new selection (via valuesChanged diff)
  // allValues.operatorId holds the OLD server value — only use as fallback when not in data
  if (!transformed.operatorId && allValues?.operatorId) {
    transformed.operatorId = allValues.operatorId;
  }

  // Handle grievance level settings
  // Convert flat form fields (grievanceMinAge1, etc.) to nested objects (grievance1: { minAge, maxAge, addition })
  // transformed has the user's changed values (form diff); allValues is the stale server snapshot.
  // Always prefer transformed first so a level change by the user is not overwritten by the old server value.
  const grievanceLevel = transformed.grievanceLevel || allValues?.grievanceLevel || 'none';
  transformed.grievanceLevel = grievanceLevel;

  // Build nested grievance objects from flat form fields
  // Use transformed first (user's changes), fall back to allValues (server snapshot for untouched fields).
  if (['level1', 'level2', 'level3'].includes(grievanceLevel)) {
    transformed.grievance1 = {
      minAge: transformed.grievanceMinAge1 ?? allValues?.grievanceMinAge1 ?? null,
      maxAge: transformed.grievanceMaxAge1 ?? allValues?.grievanceMaxAge1 ?? null,
      addition: transformed.grievanceAdditionAge1 ?? allValues?.grievanceAdditionAge1 ?? null,
    };
  } else {
    transformed.grievance1 = null;
  }

  if (['level2', 'level3'].includes(grievanceLevel)) {
    transformed.grievance2 = {
      minAge: transformed.grievanceMinAge2 ?? allValues?.grievanceMinAge2 ?? null,
      maxAge: transformed.grievanceMaxAge2 ?? allValues?.grievanceMaxAge2 ?? null,
      addition: transformed.grievanceAdditionAge2 ?? allValues?.grievanceAdditionAge2 ?? null,
    };
  } else {
    transformed.grievance2 = null;
  }

  if (grievanceLevel === 'level3') {
    transformed.grievance3 = {
      minAge: transformed.grievanceMinAge3 ?? allValues?.grievanceMinAge3 ?? null,
      maxAge: transformed.grievanceMaxAge3 ?? allValues?.grievanceMaxAge3 ?? null,
      addition: transformed.grievanceAdditionAge3 ?? allValues?.grievanceAdditionAge3 ?? null,
    };
  } else {
    transformed.grievance3 = null;
  }

  // Remove flat grievance fields (form-only, not sent to backend)
  delete transformed.grievanceMinAge1;
  delete transformed.grievanceMaxAge1;
  delete transformed.grievanceAdditionAge1;
  delete transformed.grievanceMinAge2;
  delete transformed.grievanceMaxAge2;
  delete transformed.grievanceAdditionAge2;
  delete transformed.grievanceMinAge3;
  delete transformed.grievanceMaxAge3;
  delete transformed.grievanceAdditionAge3;

  // Handle destinations array from checkbox map
  const destSource = transformed.__destinations || allValues?.__destinations;
  if (destSource) {
    const destinations = Object.entries(destSource)
      .filter(([, v]) => v === true)
      .map(([k]) => k);
    transformed.destinations = destinations;
  }

  // Handle travel profiles array from checkbox map
  const profSource = transformed.__travelProfiles || allValues?.__travelProfiles;
  if (profSource) {
    const travelProfiles = Object.entries(profSource)
      .filter(([, v]) => v === true)
      .map(([k]) => k);
    transformed.travelProfiles = travelProfiles;
  }

  // Handle coverages array — only when coverages are explicitly in the payload.
  // Skipping when absent prevents partial saves (e.g. PricesTableModal) from wiping coverages.
  if ('coverages' in transformed) {
    // For API-created plans: if the form sends [] (empty array is truthy but empty), fall back to the
    // server's allValues.coverages — avoids losing API-synced coverages when the form store is stale.
    // For non-API plans: always use transformed.coverages so intentional "delete all" is honoured.
    const isApiCreated = !!allValues?._integrations?.planId;
    const rawCoverages = (
      transformed.coverages?.length || !isApiCreated
        ? (transformed.coverages || [])
        : (allValues?.coverages || [])
    ).filter(c => c?.coverageKey);
    const standardKeys = new Set(plansService.getCoverageConfig().map(k => k.value));

    transformed.coverages = rawCoverages.map(c => {

      // Non-standard keys (e.g. MD5 hashes from API-synced plans): preserve original type/text/highlight
      if (!standardKeys.has(c.coverageKey)) {
        return { ...c, type: c.type || 'money', highlight: c.highlight ?? false };
      }

      // Standard keys: derive type/highlight from config and clear irrelevant fields
      const type = plansService.getCoverageType(c.coverageKey);
      const highlight = plansService.getCoverageHighlight(c.coverageKey);
      const coverage = { ...c, type, highlight };

      if (type === 'money') {
        coverage.included = false;
        coverage.text = null;
      } else if (type === 'switch') {
        coverage.included = true;
        coverage.currency = null;
        coverage.amount = 0;
        coverage.text = null;
      } else if (type === 'text') {
        coverage.currency = null;
        coverage.included = false;
        coverage.amount = 0;
      }

      return coverage;
    });
  }

  return transformed;
};

export default decorateRequest;
